<template>
  <div class="flex flex-col gap-4 w-full">
    <div class="flex flex-col w-full">
      <div
        @click="toggle('empresa')"
        class="header-title mt-2"
        :class="show.empresa ? 'rounded-b-none bg-gray-200' : ''"
      >
        <div class="flex flex-col text-left">
          <span class="font-bold text-primary capitalize">Empresas</span>
        </div>
        <arrow-icon :class="show.empresa ? 'rotate-180' : ''" color="#000" />
      </div>
      <div v-if="show.empresa || isLoading.empresa" class="my-2">
        <DsPolicyDomainSkeleton v-if="isLoading.empresa" />
        <PolicyDomainTab v-else :policies="empresaPolicies" />
      </div>
    </div>

    <div class="flex flex-col w-full">
      <div
        @click="toggle('unidade')"
        class="header-title"
        :class="show.unidade ? 'rounded-b-none bg-gray-200' : ''"
      >
        <div class="flex flex-col text-left">
          <span class="font-bold text-primary capitalize">Unidades</span>
        </div>
        <arrow-icon :class="show.unidade ? 'rotate-180' : ''" color="#000" />
      </div>
      <div v-if="show.unidade || isLoading.unidade" class="my-2 ml-4">
        <DsPolicyDomainSkeleton v-if="isLoading.unidade" />
        <PolicyDomainTab v-else :policies="unidadePolicies" />
      </div>
    </div>

    <div class="flex flex-col w-full">
      <div
        @click="toggle('escala')"
        class="header-title"
        :class="show.escala ? 'rounded-b-none bg-gray-200' : ''"
      >
        <div class="flex flex-col text-left">
          <span class="font-bold text-primary capitalize">Escalas</span>
        </div>
        <arrow-icon :class="show.escala ? 'rotate-180' : ''" color="#000" />
      </div>
      <div v-if="show.escala || isLoading.escala" class="my-2 ml-8">
        <DsPolicyDomainSkeleton v-if="isLoading.escala" />
        <PolicyDomainTab v-else :policies="escalaPolicies" />
      </div>
    </div>

    <div class="flex flex-col w-full">
      <div
        @click="toggle('cadeira')"
        class="header-title"
        :class="show.cadeira ? 'rounded-b-none bg-gray-200' : ''"
      >
        <div class="flex flex-col text-left">
          <span class="font-bold text-primary capitalize">Posições/Cadeiras</span>
        </div>
        <arrow-icon :class="show.cadeira ? 'rotate-180' : ''" color="#000" />
      </div>
      <div v-if="show.cadeira || isLoading.cadeira" class="my-2 ml-12">
        <DsPolicyDomainSkeleton v-if="isLoading.cadeira" />
        <PolicyDomainTab v-else :policies="cadeiraPolicies" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import ArrowIcon from '@/assets/icons/down.vue'
import PolicyDomainTab from './domain.tab.vue'
import DsPolicyDomainSkeleton from '@/components/designSystem/Skeleton/DsPolicyDomainSkeleton.vue'
import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'
import { MappedDomainPolicy } from '@/types/domain'
import { useErrorToast } from '@/composables/useErrorToast'

const { notifyError } = useErrorToast()

const DOMAIN_LOAD_ERROR: Record<string, string> = {
  empresa: 'Não foi possível carregar as empresas vinculadas à política de restrição.',
  unidade: 'Não foi possível carregar as unidades vinculadas à política de restrição.',
  escala: 'Não foi possível carregar as escalas vinculadas à política de restrição.',
  cadeira: 'Não foi possível carregar as posições vinculadas à política de restrição.'
}

const show = ref({
  empresa: false,
  unidade: false,
  escala: false,
  cadeira: false
})

const isLoading = reactive({
  empresa: false,
  unidade: false,
  escala: false,
  cadeira: false
})

const toggle = async (key: keyof typeof show.value) => {
  if (!show.value[key]) {
    try {
      isLoading[key] = true
      await loadPoliciesByDomain(key)
    } catch (e) {
      console.error(`Erro ao carregar políticas de ${key}`, e)
    } finally {
      isLoading[key] = false
      show.value[key] = !show.value[key]
    }
  } else {
    show.value[key] = !show.value[key]
  }
}

const policyStore = useDsPolicyStore()

const empresaPolicies = ref<MappedDomainPolicy[]>([])
const unidadePolicies = ref<MappedDomainPolicy[]>([])
const escalaPolicies = ref<MappedDomainPolicy[]>([])
const cadeiraPolicies = ref<MappedDomainPolicy[]>([])

const formatDate = (str: string) => {
  const date = new Date(str)
  return isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR')
}

// eslint-disable-next-line complexity
const loadPoliciesByDomain = async (domainType: string) => {
  const policy = policyStore.currentPolicy
  if (!policy) return

  let domainList: any[] = []

  try {
    switch (domainType) {
      case 'empresa':
        domainList = (await policyStore.fetchCompanyDomain(policy.id))?.data || []
        break
      case 'unidade':
        domainList = (await policyStore.fetchHealthUnitDomain(policy.id))?.data || []
        break
      case 'escala':
        domainList = (await policyStore.fetchScheduleDomain(policy.id))?.data || []
        break
      case 'cadeira':
        domainList = (await policyStore.fetchPositionDomain(policy.id))?.data || []
        break
    }

    const mapped: MappedDomainPolicy[] = domainList.map((d) => ({
      id: d.id,
      local:
        d.company?.name ||
        d.healthUnit?.name ||
        d.schedule?.name ||
        d.position?.name ||
        'Desconhecido',
      startDate: formatDate(d.startDate),
      endDate: formatDate(d.endDate),
      vinculoDate: formatDate(d.createdAt),
      responsavel: d.linkedBy?.name || 'Desconhecido'
    }))

    if (domainType === 'empresa') empresaPolicies.value = mapped
    if (domainType === 'unidade') unidadePolicies.value = mapped
    if (domainType === 'escala') escalaPolicies.value = mapped
    if (domainType === 'cadeira') cadeiraPolicies.value = mapped
  } catch (e) {
    console.error(`Erro ao buscar domínio ${domainType}`, e)
    notifyError(
      e,
      DOMAIN_LOAD_ERROR[domainType] ??
        'Não foi possível carregar o domínio da política de restrição.'
    )
  }
}
</script>

<style scoped>
.header-title {
  @apply flex justify-between items-center gap-4 w-full px-[12px] py-[16px] rounded-lg bg-gray-100 text-black text-[16px] font-bold cursor-pointer capitalize;
}
</style>
